import * as React from 'react';
import { CSSProperties } from 'react';
import { ApplicationStore } from '../../data_layer/stores/ApplicationStore';
import { CONSTANTS } from '../../assets/UIConstants';
import * as assert from "assert";
import { TransitionGroup } from 'react-transition-group'
import { Fade } from 'material-ui';
import Dialog, { DialogContent, DialogContentText } from 'material-ui/Dialog/index';
import { BigBlueButton } from './BigBlueButton';
import {
    HELP_MODAL_CLOSE_BUTTON_TEXT, HELP_MODAL_CONFIRM_BUTTON_TEXT,
    HELP_MODAL_CONFIRMATION, HELP_MODAL_INFO_TEXT, HELP_MODAL_SERVER_ERROR_MESSAGE,
    HELP_MODAL_TITLE
} from '../../assets/ApplicationCopy';

interface Props {
    store: ApplicationStore;
    open: boolean;
    close: () => void;
    onError: (message: string) => void;
}

interface State {
    wasRequestSent: boolean;
    isRequestPending: boolean;
    selectedTopic?: HelpTopic;
}

enum HelpTopic {
    Improving = 'Improving my results',
    Understanding = 'Understanding my results',
    Information = 'Getting more information about my results',
}


export class NeedHelpDialog extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);
        this.state = {
            wasRequestSent: false,
            isRequestPending: false,
            selectedTopic: undefined,
        };
    }

    private selectedButtonStyle(topic: HelpTopic) {
        if (this.state.selectedTopic === topic) {
            return selectedButtonStyle;
        }
        return helpModalButtonStyle;
    }

    private requestHelp(): void {
        assert(undefined != this.state.selectedTopic, 'Dashboard#requestHelp - no topic selected');
        this.setState({ isRequestPending: true });
        const scanID = this.props.store.scanStore.mostRecentScan.id;
        this.props.store.scanStore.requestHelp(scanID, this.state.selectedTopic!.toString())
            .then(() => {
                this.setState({ wasRequestSent: true });
            })
            .catch(() => {
                this.props.onError(HELP_MODAL_SERVER_ERROR_MESSAGE);
            });
    }

    public render(): JSX.Element {
        return (
            <Dialog
                title={HELP_MODAL_TITLE}
                open={this.props.open}
                onClose={() => { this.props.close(); }}
                fullWidth={true}
                maxWidth={'md'}
            >
                <DialogContent>
                    <div style={helpModalContentArea}>
                        {false == this.state.wasRequestSent ?
                            <DialogContentText
                                style={helpModalText}
                            >
                                {HELP_MODAL_TITLE}
                            </DialogContentText>
                            :
                            <div style={{ height: 230 }} />
                        }
                        <TransitionGroup
                            className="helpRequestSent"
                        >
                            {this.state.wasRequestSent && false === this.state.isRequestPending &&
                                <Fade
                                    key={1}
                                    in={this.state.wasRequestSent && false === this.state.isRequestPending}
                                    timeout={500}
                                    unmountOnExit={true}
                                >
                                    <div style={helpModalButtonsContainer}>
                                        <DialogContentText
                                            style={helpModalConfirmationText}
                                        >
                                            {HELP_MODAL_CONFIRMATION}
                                        </DialogContentText>
                                        <BigBlueButton
                                            style={helpModalButtonStyle}
                                            onClick={() => { this.props.close(); }}
                                        >
                                            {HELP_MODAL_CLOSE_BUTTON_TEXT}
                                        </BigBlueButton>
                                    </div>
                                </Fade>
                            }

                            {false == this.state.wasRequestSent &&
                                <Fade
                                    key={2}
                                    in={false == this.state.wasRequestSent}
                                    timeout={500}
                                    onExited={() => this.setState({ isRequestPending: false })}
                                    unmountOnExit={true}
                                >
                                    <div style={helpModalButtonsContainer}>
                                        <BigBlueButton
                                            style={this.selectedButtonStyle(HelpTopic.Improving)}
                                            disabled={this.state.isRequestPending}
                                            onClick={() => this.setState({ selectedTopic: HelpTopic.Improving })}
                                        >
                                            {HelpTopic.Improving}
                                        </BigBlueButton>

                                        <BigBlueButton
                                            style={this.selectedButtonStyle(HelpTopic.Understanding)}
                                            disabled={this.state.isRequestPending}
                                            onClick={() => this.setState({ selectedTopic: HelpTopic.Understanding })}
                                        >
                                            {HelpTopic.Understanding}
                                        </BigBlueButton>

                                        <BigBlueButton
                                            style={this.selectedButtonStyle(HelpTopic.Information)}
                                            disabled={this.state.isRequestPending}
                                            onClick={() => this.setState({ selectedTopic: HelpTopic.Information })}
                                        >
                                            {HelpTopic.Information}
                                        </BigBlueButton>

                                        <DialogContentText style={helpModalInfoTextStyle}>
                                            {HELP_MODAL_INFO_TEXT}
                                        </DialogContentText>

                                        <BigBlueButton
                                            style={helpModalConfirmButtonStyle}
                                            disabled={this.state.isRequestPending || undefined == this.state.selectedTopic}
                                            onClick={() => this.requestHelp()}
                                        >
                                            {HELP_MODAL_CONFIRM_BUTTON_TEXT}
                                        </BigBlueButton>
                                    </div>
                                </Fade>
                            }
                        </TransitionGroup>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }
}
const helpModalContentArea: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 660 + 'px',
};

const helpModalButtonsContainer: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    paddingRight: 180 + 'px',
    paddingLeft: 180 + 'px',
    marginBottom: 80 + 'px',
};

const helpModalButtonStyle: CSSProperties = {
    fontSize: 18 + 'px',
    fontWeight: 'bold',
    textTransform: 'none',
    marginTop: 20 + 'px',
    marginBottom: 20 + 'px',
};

const selectedButtonStyle: CSSProperties = {
    ...helpModalButtonStyle,
    backgroundColor: CONSTANTS.COLORS.THEME_ORANGE
};
const helpModalConfirmButtonStyle: CSSProperties = {
    ...helpModalButtonStyle,
    width: 160 + 'px',
    marginLeft: 'auto',
    marginRight: 'auto',
};

const helpModalText: CSSProperties = {
    fontSize: 24 + 'px',
    fontWeight: 'bold',
    textTransform: 'none',
    textAlign: 'center',
    color: 'black',
    marginBottom: 40 + 'px',
    marginTop: 40 + 'px',
};

const helpModalInfoTextStyle: CSSProperties = {
    marginTop: 20 + 'px',
};

const helpModalConfirmationText: CSSProperties = {
    fontSize: 24 + 'px',
    fontWeight: 'bold',
    textTransform: 'none',
    textAlign: 'center',
    color: CONSTANTS.COLORS.THEME_BLUE,
    marginBottom: 40 + 'px',
    marginTop: 40 + 'px',
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/components/NeedHelpDialog.tsx